import { VIDEO_SERVER_BASE_URL } from "../config/videoServer";
import { getAccessVisitorId, type AccessIdentity } from "./accessTracking";

export type VideoComment = {
  id: string;
  timestamp: string;
  userLabel: string;
  commentText: string;
  editedAt?: string;
};

export type VideoActionState = {
  favorited: boolean;
  commentCount: number;
  comments: VideoComment[];
};

const identityParams = (identity: AccessIdentity) => ({
  userId: identity?.uid || "",
  userLabel: identity?.displayName || (identity?.uid ? "登录用户" : "匿名访客"),
  visitorId: getAccessVisitorId(),
});

async function responseJson<T>(response: Response): Promise<T> {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `请求失败 (${response.status})`);
  return body as T;
}

export async function fetchVideoActionState(
  videoId: string,
  identity: AccessIdentity,
): Promise<VideoActionState> {
  const params = new URLSearchParams(identityParams(identity));
  return responseJson<VideoActionState>(
    await fetch(`${VIDEO_SERVER_BASE_URL}/api/video-actions/${encodeURIComponent(videoId)}?${params}`, {
      cache: "no-store",
      credentials: "include",
    }),
  );
}

async function submitVideoAction(
  videoId: string,
  videoTitle: string,
  identity: AccessIdentity,
  action: "favorite" | "unfavorite" | "comment",
  commentText = "",
) {
  return responseJson<{ accepted: boolean; event: string; timestamp: string }>(
    await fetch(`${VIDEO_SERVER_BASE_URL}/api/video-actions`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...identityParams(identity),
        videoId,
        videoTitle,
        action,
        commentText,
        path: typeof window !== "undefined" ? window.location.href : "native-app",
        language: typeof navigator !== "undefined" ? navigator.language : "",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      }),
    }),
  );
}

export const setVideoFavorite = (
  videoId: string,
  videoTitle: string,
  identity: AccessIdentity,
  favorite: boolean,
) => submitVideoAction(videoId, videoTitle, identity, favorite ? "favorite" : "unfavorite");

export const addVideoComment = (
  videoId: string,
  videoTitle: string,
  identity: AccessIdentity,
  commentText: string,
) => submitVideoAction(videoId, videoTitle, identity, "comment", commentText.trim());

export async function updateVideoComment(
  videoId: string,
  commentId: string,
  commentText: string,
) {
  return responseJson<VideoComment>(
    await fetch(
      `${VIDEO_SERVER_BASE_URL}/api/admin/video-comments/${encodeURIComponent(commentId)}`,
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ videoId, commentText: commentText.trim() }),
      },
    ),
  );
}

export async function deleteVideoComment(videoId: string, commentId: string) {
  const response = await fetch(
    `${VIDEO_SERVER_BASE_URL}/api/admin/video-comments/${encodeURIComponent(commentId)}?videoId=${encodeURIComponent(videoId)}`,
    { method: "DELETE", credentials: "include" },
  );
  if (!response.ok) await responseJson(response);
}
